/** Persistent village presets. The realm pays for levels and owns their production. */
import { buildingType, buildingTypeById, buildingTypeIndex, GATE_DEF, TOWER_DEF, wallTierDef } from '../../data/buildings.ts';
import { NodeKind, nodeKind } from '../../data/nodes.ts';
import { VILLAGER_TYPE } from '../../data/units.ts';
import { RESOURCE_KEYS, Team, Terrain, WallKind } from '../types.ts';
import { placeBuilding, recomputeManaCap, recomputePopCap, recomputeStorage, spawnUnit, setWall, type World } from '../world.ts';
import { recount } from '../systems/towers.ts';
import { clearNodesInRect, nodeFootprintBounds, nodeFootprintClear } from '../systems/nodes.ts';
import { paintBridgePlan, planBridge, type BridgePlan } from '../bridges.ts';
import { isVillageHolding, villageLevel, villageLevelStats, villageTypeOf, type VillageType } from './villages.ts';

export const VILLAGE_WALL_RADIUS = 14;
export interface VillagePlot { id: string; x: number; y: number; level: number }
const TRADE: Record<VillageType, readonly [string, string]> = {
  farming: ['farm', 'mill'], woodcutting: ['woodcutter', 'sawmill'],
  stone_quarry: ['quarry', 'storehouse'], iron_mining: ['iron_mine', 'smelter'],
  fishing: ['fishery', 'granary'], soul_shrine: ['chapel', 'chapel'],
  gold_mining: ['iron_mine', 'quarry'],
};
const ROOFS = [[-7,-7],[5,-7],[-7,5],[5,5],[-11,-7],[9,5],[9,-7],[-11,5],
  [-7,-11],[5,9],[5,-11],[-7,9],[-11,-11],[9,9],[9,-11],[-11,9]] as const;

/** Previewable, additive programme: a roof's coordinates never change at a later level. */
export function villageLayoutPlan(type: VillageType, level: number): VillagePlot[] {
  const n = Math.max(1, Math.min(7, Math.floor(level))), [main, secondary] = TRADE[type];
  const plots: VillagePlot[] = ROOFS.map(([x,y], i) => ({ id: 'house', x, y, level: Math.max(1, Math.floor(i / 2)) }));
  plots.push({ id:'storehouse', x:-8, y:-3, level:1 }, { id:'granary', x:5, y:-3, level:2 },
    { id:main, x:-21, y:-8, level:1 }, { id:main, x:18, y:6, level:3 },
    { id:secondary, x:-21, y:6, level:4 }, { id:main, x:18, y:-8, level:5 },
    { id:'watchtower', x:-3, y:6, level:6 }, { id:main, x:-8, y:18, level:7 });
  return plots.filter(p => p.level <= n).sort((a,b) => a.level-b.level);
}

export function villageLayoutSummary(type: VillageType, level: number): { roofs: number; wallTiles: number; gates: number; towers: number; wallTier: number } {
  const cells = wallPlan(level);
  return { roofs: villageLayoutPlan(type, level).length + 1,
    wallTiles: cells.filter(p => p.kind === WallKind.Wall).length,
    gates: cells.filter(p => p.kind === WallKind.Gate).length,
    towers: Math.floor(cells.filter(p => p.kind === WallKind.Tower).length / 4), wallTier: wallTier(level) };
}

function wallTier(level: number): number { return 1 + villageLevelStats('farming', level).fort; }
function wallHealth(kind: number, tier: number, level: number): number {
  return (kind===WallKind.Gate ? GATE_DEF.hp : kind===WallKind.Tower ? TOWER_DEF.hp : wallTierDef(tier).hp) * (1+(level-1)*0.12);
}
interface WallCell { x: number; y: number; kind: number }
function wallPlan(level: number): WallCell[] {
  const r = VILLAGE_WALL_RADIUS, cells = new Map<string, WallCell>();
  for (let v=-r; v<=r; v++) for (const [x,y] of [[v,-r],[v,r],[-r,v],[r,v]]) {
    const kind = Math.abs(v) <= 1 ? WallKind.Gate : WallKind.Wall;
    cells.set(`${x}/${y}`, { x, y, kind });
  }
  const corners = [[-r,-r],[r-1,r-1],[r-1,-r],[-r,r-1]];
  const count = level >= 7 ? 4 : level >= 5 ? 3 : level >= 3 ? 2 : level >= 2 ? 1 : 0;
  for (const [x,y] of corners.slice(0,count)) for (let dy=0;dy<2;dy++) for (let dx=0;dx<2;dx++) {
    cells.set(`${x+dx}/${y+dy}`, { x:x+dx, y:y+dy, kind:WallKind.Tower });
  }
  return [...cells.values()];
}

function dry(w: World, x: number, y: number): boolean {
  if (!w.map.inBounds(x,y)) return false;
  const t = w.map.terrain[w.map.idx(x,y)];
  return t !== Terrain.River && t !== Terrain.Deep && t !== Terrain.Marsh && t !== Terrain.Bridge;
}

const CARDINAL = [[0,-1],[-1,0],[1,0],[0,1]] as const;

/** Natural land connectivity ignores bridges: a crossing is not a building site. */
function villageBank(w: World): Uint8Array {
  const map=w.map,bank=new Uint8Array(map.terrain.length),queue=new Int32Array(bank.length);
  const start=map.idx(map.hearthX,map.hearthY);
  if(!dry(w,map.hearthX,map.hearthY)) return bank;
  bank[start]=1;queue[0]=start;
  let head=0,tail=1;
  while(head<tail) {
    const k=queue[head++],x=k%map.w,y=Math.floor(k/map.w);
    for(const [dx,dy] of CARDINAL) {
      const xx=x+dx,yy=y+dy;
      if(!dry(w,xx,yy)) continue;
      const next=map.idx(xx,yy);if(bank[next]) continue;
      bank[next]=1;queue[tail++]=next;
    }
  }
  return bank;
}

/** Site only a freshly founded village. Played and legacy towns never move. */
export function siteVillageOnBank(w: World): void {
  if(w.villageLevel!==0||w.tick!==0||w.buildings.slots.count!==1||w.hearth<0) return;
  const map=w.map,ox=map.hearthX,oy=map.hearthY,bank=villageBank(w),stride=map.w+1;
  const wet=new Uint32Array(stride*(map.h+1)),stone=new Uint32Array(wet.length);
  const distance=new Uint16Array(map.terrain.length).fill(65535),queue=new Int32Array(distance.length);
  let head=0,tail=0;
  for(let y=0;y<map.h;y++) {
    let wetRow=0,stoneRow=0;
    for(let x=0;x<map.w;x++) {
      const k=map.idx(x,y),t=map.terrain[k],at=(y+1)*stride+x+1;
      wetRow+=Number(!dry(w,x,y));stoneRow+=w.nodes.solidTiles[k];
      wet[at]=wet[at-stride]+wetRow;stone[at]=stone[at-stride]+stoneRow;
      if(t===Terrain.River||t===Terrain.Deep||t===Terrain.Bridge) {distance[k]=0;queue[tail++]=k;}
    }
  }
  while(head<tail) {
    const k=queue[head++],x=k%map.w,y=Math.floor(k/map.w),d=distance[k]+1;
    for(const [dx,dy] of CARDINAL) {
      const xx=x+dx,yy=y+dy;if(!map.inBounds(xx,yy)) continue;
      const next=map.idx(xx,yy);if(distance[next]<=d) continue;
      distance[next]=d;queue[tail++]=next;
    }
  }
  const count=(grid:Uint32Array,x:number,y:number,r:number):number=>
    grid[(y+r+1)*stride+x+r+1]-grid[(y-r)*stride+x+r+1]-grid[(y+r+1)*stride+x-r]+grid[(y-r)*stride+x-r];
  const margin=VILLAGE_WALL_RADIUS+2;
  // A river bend can isolate the generic start on a small island (including
  // the default "hearth" seed). Prefer that bank only if the full final ring
  // fits; otherwise found the village on a nearby, genuinely usable landmass.
  const selectSite=(sameBank:boolean):{x:number;y:number;score:number}|null=> {
    let best:{x:number;y:number;score:number}|null=null;
    for(let y=Math.max(36,oy-72);y<=Math.min(map.h-37,oy+72);y+=2) for(let x=Math.max(36,ox-72);x<=Math.min(map.w-37,ox+72);x+=2) {
      const k=map.idx(x,y);
      if((sameBank&&!bank[k])||count(wet,x,y,margin)>0||count(stone,x,y,6)>0) continue;
      // A broad dry bank near the water, with the full final circuit on land.
      // Castle Acre's gate street and Goodrich's position above a crossing inform
      // the geometry; no river tiles are filled to manufacture a building site.
      const shore=distance[k]===65535 ? 0 : (distance[k]-24)**2;
      const score=(x-ox)**2+(y-oy)**2+shore*2;
      if(!best||score<best.score) best={x,y,score};
    }
    return best;
  };
  const best=selectSite(true)??selectSite(false);
  if(!best||(best.x===ox&&best.y===oy)) return;
  const b=w.buildings,i=w.hearth,def=buildingType(b.typeId[i]),tx=b.tx[i],ty=b.ty[i];
  for(let y=ty;y<ty+def.h;y++) for(let x=tx;x<tx+def.w;x++) {
    const k=map.idx(x,y);if(map.building[k]===i) map.building[k]=-1;
    if(map.terrain[k]===Terrain.Ruin) map.terrain[k]=Terrain.AshPlain;
  }
  const dx=best.x-ox,dy=best.y-oy;
  b.tx[i]+=dx;b.ty[i]+=dy;map.hearthX=best.x;map.hearthY=best.y;
  clearNodesInRect(w.nodes,b.tx[i],b.ty[i],b.tx[i]+def.w-1,b.ty[i]+def.h-1);
  for(let y=b.ty[i];y<b.ty[i]+def.h;y++) for(let x=b.tx[i];x<b.tx[i]+def.w;x++) {
    const k=map.idx(x,y);map.building[k]=i;map.terrain[k]=Terrain.Ruin;
  }
  for(let j=0;j<w.units.slots.high;j++) if(w.units.isAlive(j)) {
    w.units.x[j]+=dx;w.units.y[j]+=dy;w.units.goalX[j]+=dx;w.units.goalY[j]+=dy;
  }
  map.terrainVersion++;w.dirty.flow=true;w.dirty.jobs=true;w.dirty.districts=true;
}

/** A short dry route; never turn a river into a road or cut through a deposit. */
function villageRoad(w: World, from: readonly [number,number], to: readonly [number,number], bank?: Uint8Array): Array<[number,number]> {
  const map=w.map,parent=new Int32Array(map.terrain.length).fill(-1),queue=new Int32Array(parent.length);
  const start=map.idx(from[0],from[1]),goal=map.idx(to[0],to[1]);
  if(!map.inBounds(...from)||!map.inBounds(...to)) return [];
  const walk=(x:number,y:number):boolean=> {
    if(!dry(w,x,y)||Math.abs(x-from[0])>100||Math.abs(y-from[1])>100) return false;
    const k=map.idx(x,y);
    return (!bank||bank[k]===1)&&map.building[k]<0&&!map.rubble[k]&&!w.nodes.solidTiles[k]&&(!map.wall[k]||map.wall[k]===WallKind.Gate);
  };
  if(!walk(...from)||!walk(...to)) return [];
  parent[start]=start;queue[0]=start;let head=0,tail=1;
  while(head<tail&&parent[goal]<0) {
    const k=queue[head++],x=k%map.w,y=Math.floor(k/map.w);
    // Axis toward the destination first: the street bends as little as needed.
    const directions=[...CARDINAL].sort(([ax,ay],[bx,by])=>
      Math.abs(x+ax-to[0])+Math.abs(y+ay-to[1])-Math.abs(x+bx-to[0])-Math.abs(y+by-to[1]));
    for(const [dx,dy] of directions) {
      const xx=x+dx,yy=y+dy;if(!walk(xx,yy)) continue;
      const next=map.idx(xx,yy);if(parent[next]>=0) continue;
      parent[next]=k;queue[tail++]=next;
    }
  }
  if(parent[goal]<0) return [];
  const path:Array<[number,number]>=[];
  for(let k=goal;;k=parent[k]) {path.push([k%map.w,Math.floor(k/map.w)]);if(k===start) break;}
  return path.reverse();
}

function paintRoad(w: World, path: Array<[number,number]>): void {
  for(const [x,y] of path) {
    const k=w.map.idx(x,y);if(!dry(w,x,y)||w.map.terrain[k]===Terrain.Leyline) continue;
    clearNodesInRect(w.nodes,x,y,x,y);w.map.terrain[k]=Terrain.Road;
  }
}

function villageGate(w: World, point: readonly [number,number]): [number,number] {
  const {hearthX:cx,hearthY:cy}=w.map,r=VILLAGE_WALL_RADIUS;
  const gates:Array<[number,number]>=[[cx-r,cy],[cx+r,cy],[cx,cy-r],[cx,cy+r]];
  gates.sort((a,b)=>(a[0]-point[0])**2+(a[1]-point[1])**2-(b[0]-point[0])**2-(b[1]-point[1])**2);
  return gates[0];
}

/** One controlled crossing, connected to the dry gate street on both banks. */
function villageStreets(w: World, bank: Uint8Array): void {
  const map=w.map,cx=map.hearthX,cy=map.hearthY,r=VILLAGE_WALL_RADIUS;
  const choices:Array<{plan:BridgePlan;near:[number,number];far:[number,number];score:number}>=[],seen=new Set<string>();
  for(let y=Math.max(2,cy-64);y<=Math.min(map.h-3,cy+64);y+=2) for(let x=Math.max(2,cx-64);x<=Math.min(map.w-3,cx+64);x+=2) {
    const t=map.terrain[map.idx(x,y)];if(t!==Terrain.River&&t!==Terrain.Deep&&t!==Terrain.Bridge) continue;
    const plan=planBridge(map,x,y);if(!plan.valid||!plan.banks) continue;
    const [a,b]=plan.banks,ka=map.idx(...a),kb=map.idx(...b);
    if(bank[ka]===bank[kb]) continue;
    const near=bank[ka] ? a : b,far=bank[ka] ? b : a,key=plan.tiles[0].join('/');
    if(seen.has(key)) continue;seen.add(key);
    choices.push({plan,near,far,score:(near[0]-cx)**2+(near[1]-cy)**2+plan.tiles.length*6});
  }
  choices.sort((a,b)=>a.score-b.score);
  let bridge:typeof choices[number]|undefined;
  for(const choice of choices.slice(0,16)) {
    const road=villageRoad(w,villageGate(w,choice.near),choice.near,bank);
    if(!road.length) continue;
    paintBridgePlan(map,w.nodes,choice.plan);paintRoad(w,road);bridge=choice;
    const dx=Math.sign(choice.far[0]-choice.near[0]),dy=Math.sign(choice.far[1]-choice.near[1]);
    for(let distance=8;distance>=2;distance--) {
      const beyond:[number,number]=[choice.far[0]+dx*distance,choice.far[1]+dy*distance];
      const onward=villageRoad(w,choice.far,beyond);
      if(onward.length) {paintRoad(w,onward);break;}
    }
    break;
  }
  const green:[number,number]=[cx-3,cy+3];
  const alongY=bridge?.plan.axis==='x';
  const gates:Array<[number,number]>=alongY ? [[cx,cy-r],[cx,cy+r]] : [[cx-r,cy],[cx+r,cy]];
  if(bridge) gates.push(villageGate(w,bridge.near));
  for(const gate of gates) paintRoad(w,villageRoad(w,gate,green,bank));
}

interface ResourcePlot { kind: number; points: Array<{x:number;y:number}>; score: number }
const GROVE = [[-2,-1],[0,-2],[2,-1],[-3,1],[-1,1],[1,1],[3,1],[0,3]] as const;

function plotResource(id: string): number {
  return id==='woodcutter' ? NodeKind.Tree : id==='quarry' ? NodeKind.Rock : id==='iron_mine' ? NodeKind.Ore : -1;
}

/** Do not seed over streets, future battlements, or a ruined roof awaiting recovery. */
function resourceGround(w: World, x: number, y: number, bank: Uint8Array): boolean {
  if(!dry(w,x,y)) return false;
  const k=w.map.idx(x,y),t=w.map.terrain[k],cx=w.map.hearthX,cy=w.map.hearthY;
  if(!bank[k]) return false;
  if(w.map.building[k]>=0||w.map.wall[k]||w.map.rubble[k]||t===Terrain.Road||t===Terrain.Bridge||t===Terrain.Leyline) return false;
  const r=VILLAGE_WALL_RADIUS,dx=x-cx,dy=y-cy;
  if(Math.abs(dx)<=r&&Math.abs(dy)<=r&&(Math.abs(dx)>=r-1||Math.abs(dy)>=r-1)) return false;
  return !(w.villageBlueprint?.roofs.some(roof=>{
    const def=buildingType(roof.type);
    return x>=roof.x&&x<roof.x+def.w&&y>=roof.y&&y<roof.y+def.h;
  }) ?? false);
}

/** Select existing nearby resources first; otherwise reserve one coherent deposit or grove. */
function resourcePlot(w: World, kind: number, x: number, y: number, bw: number, bh: number, nearby: Int32Array, bank: Uint8Array): ResourcePlot|null {
  let closest=Infinity,trees=0;
  for(const n of nearby) {
    if(n<0) break;
    const r=nodeFootprintBounds(w.nodes,n);
    let clear=true;
    for(let yy=r.y0;yy<=r.y1&&clear;yy++) for(let xx=r.x0;xx<=r.x1;xx++) if(!resourceGround(w,xx,yy,bank)) {clear=false;break;}
    if(!clear) continue;
    const dx=Math.max(x-r.x1-1,r.x0-x-bw,0),dy=Math.max(y-r.y1-1,r.y0-y-bh,0),d2=dx*dx+dy*dy;
    if(d2<=49) trees++;
    closest=Math.min(closest,d2);
  }
  if(closest<=9&&(kind!==NodeKind.Tree||trees>=5)) return {kind,points:[],score:closest};

  const radius=kind===NodeKind.Tree ? 3 : Math.floor(nodeKind(kind).footprint/2);
  const mx=x+Math.floor((bw-1)/2),my=y+Math.floor((bh-1)/2);
  // Far side first: the workshop remains in front of its deposit, with one
  // complete walkable tile between the building and the natural footprint.
  const sides=[{x:mx,y:y-radius-2,dx:1,dy:0},{x:x-radius-2,y:my,dx:0,dy:1},
    {x:x+bw+radius+1,y:my,dx:0,dy:1},{x:mx,y:y+bh+radius+1,dx:1,dy:0}];
  for(const side of sides) for(const shift of [0,-2,2,-4,4]) {
    const cx=side.x+side.dx*shift,cy=side.y+side.dy*shift;
    if(kind===NodeKind.Tree) {
      const points=GROVE.map(([dx,dy])=>({x:cx+dx,y:cy+dy})).filter(p=>{
        if(!resourceGround(w,p.x,p.y,bank)) return false;
        // Irregular groves may wrap around an obstacle, but never the camp.
        if(p.x>=x-1&&p.x<=x+bw&&p.y>=y-1&&p.y<=y+bh) return false;
        const n=w.nodes.at(w.map.idx(p.x,p.y));
        return n<0||w.nodes.kind[n]===NodeKind.Tree;
      });
      if(points.length>=6) return {kind,points,score:16+shift*shift};
    } else {
      if(!nodeFootprintClear(w.map,w.nodes,cx,cy,kind)) continue;
      let clear=true;
      for(let yy=cy-radius;yy<=cy+radius&&clear;yy++) for(let xx=cx-radius;xx<=cx+radius;xx++) {
        if(!resourceGround(w,xx,yy,bank)||(xx>=x-1&&xx<=x+bw&&yy>=y-1&&yy<=y+bh)) {clear=false;break;}
      }
      if(clear) return {kind,points:[{x:cx,y:cy}],score:16+shift*shift};
    }
  }
  return null;
}

function stampPlot(w: World, plot: VillagePlot, bank: Uint8Array): number {
  const def = buildingTypeById(plot.id), cx = w.map.hearthX + plot.x, cy = w.map.hearthY + plot.y;
  const kind=plotResource(plot.id),nearby=new Int32Array(32).fill(-1);
  if(kind>=0) w.nodes.nearestNodes(cx+def.w/2,cy+def.h/2,19,kind,nearby);
  let best: {x:number;y:number;score:number;resource:ResourcePlot|null}|null = null;
  const search = plot.id === 'fishery' ? 34 : kind>=0 ? 14 : 5;
  for (let dy=-search;dy<=search;dy++) for (let dx=-search;dx<=search;dx++) {
    const x=cx+dx,y=cy+dy;
    let okay = true;
    for (let yy=y-1;yy<=y+def.h && okay;yy++) for (let xx=x-1;xx<=x+def.w;xx++) {
      if (!dry(w,xx,yy)) { okay=false;break; }
      const k=w.map.idx(xx,yy);
      if(!bank[k]) {okay=false;break;}
      if (w.map.building[k]>=0 || w.map.wall[k] || w.map.rubble[k] || w.map.terrain[k]===Terrain.Road || w.map.terrain[k]===Terrain.Bridge) { okay=false;break; }
      const node=w.nodes.at(k);
      // Homes may clear a few trees. Natural stone faces are retained, and a
      // gatherer always stands beside its resource rather than replacing it.
      if(node>=0&&(w.nodes.kind[node]!==NodeKind.Tree||(kind>=0&&xx>=x&&xx<x+def.w&&yy>=y&&yy<y+def.h))) {okay=false;break;}
    }
    if (!okay) continue;
    if (plot.id==='fishery') {
      let bank=false;
      for(let yy=y-3;yy<=y+def.h+2;yy++) for(let xx=x-3;xx<=x+def.w+2;xx++) {
        if (!w.map.inBounds(xx,yy)) continue;
        const t=w.map.terrain[w.map.idx(xx,yy)];
        if (t===Terrain.River||t===Terrain.Deep) bank=true;
      }
      if(!bank) continue;
    }
    const resource=kind<0 ? null : resourcePlot(w,kind,x,y,def.w,def.h,nearby,bank);
    if(kind>=0&&!resource) continue;
    const score=dx*dx+dy*dy+(resource?.score ?? 0);
    if(!best||score<best.score) best={x,y,score,resource};
  }
  if(!best) return -1;
  const site=placeBuilding(w,buildingTypeIndex(plot.id),best.x,best.y,Team.Player,true);
  if(site>=0&&best.resource) markResource(w,best.resource);
  return site;
}

function markResource(w: World, plot: ResourcePlot): void {
  const def=nodeKind(plot.kind),radius=Math.floor(def.footprint/2);
  for(const {x,y} of plot.points) {
    const k=w.map.idx(x,y);
    if(w.nodes.at(k)>=0) continue;
    if(w.nodes.spawn(k,plot.kind,def.amount)<0) continue;
    for(let yy=y-radius;yy<=y+radius;yy++) for(let xx=x-radius;xx<=x+radius;xx++) w.map.terrain[w.map.idx(xx,yy)]=def.terrain;
  }
}

/** Capture actual dry plots, including ruins in old village saves. Never remove a lost entry. */
function rememberLayout(w: World): void {
  const blueprint = w.villageBlueprint ??= { roofs: [], walls: [] }, b = w.buildings;
  const roof = (type:number,x:number,y:number,tier:number,maxHp:number) => {
    const old=blueprint.roofs.find(r=>r.x===x&&r.y===y);
    if(old) Object.assign(old,{type,tier,maxHp});
    else blueprint.roofs.push({type,x,y,tier,maxHp});
  };
  for(let i=0;i<b.slots.high;i++) if(b.slots.alive[i]) roof(b.typeId[i],b.tx[i],b.ty[i],b.tier[i],b.maxHp[i]);
  for(let k=0;k<w.map.rubble.length;k++) if(w.map.rubble[k]) {
    const x=k%w.map.w,y=Math.floor(k/w.map.w),mark=w.map.rubble[k];
    if((x>0&&w.map.rubble[k-1]===mark)||(y>0&&w.map.rubble[k-w.map.w]===mark)) continue;
    if(!blueprint.roofs.some(r=>r.x===x&&r.y===y)) roof(mark-1,x,y,1,buildingType(mark-1).hp);
  }
  for(let tile=0;tile<w.map.wall.length;tile++) if(w.map.wall[tile]) {
    const next={tile,kind:w.map.wall[tile],tier:w.map.wallTier[tile],maxHp:w.map.wallMaxHp[tile]};
    const old=blueprint.walls.find(r=>r.tile===tile);
    if(old) Object.assign(old,next);else blueprint.walls.push(next);
  }
  blueprint.roofs.sort((a,b)=>a.y-b.y||a.x-b.x);
  blueprint.walls.sort((a,b)=>a.tile-b.tile);
}

/** Recovery runs by campaign month, never by visits or rendered frames. */
function recoverVillage(w: World, months: number): boolean {
  const blueprint=w.villageBlueprint;if(!blueprint||months<=0) return false;
  const share=Math.min(1,months*.25),b=w.buildings;
  let changed=false;
  for(const roof of blueprint.roofs) {
    if((roof.level ?? 1)>w.villageLevel) continue;
    const def=buildingType(roof.type),k=w.map.idx(roof.x,roof.y);
    let i=w.map.building[k];
    if(i<0) {
      let free=true;
      for(let y=roof.y;y<roof.y+def.h;y++) for(let x=roof.x;x<roof.x+def.w;x++) {
        const tile=w.map.idx(x,y);
        if(w.map.building[tile]>=0||w.map.wall[tile]) free=false;
      }
      if(!free) continue;
      for(let y=roof.y;y<roof.y+def.h;y++) for(let x=roof.x;x<roof.x+def.w;x++) {
        const tile=w.map.idx(x,y);
        if(w.map.rubble[tile]) w.rubbleCount=Math.max(0,w.rubbleCount-1);
        w.map.rubble[tile]=0;w.map.rubbleWork[tile]=0;
      }
      i=placeBuilding(w,roof.type,roof.x,roof.y,Team.Player,true);
      if(i<0) continue;
      b.tier[i]=roof.tier;b.maxHp[i]=roof.maxHp;b.hp[i]=0;
      if(def.category==='core') w.hearth=i;
    }
    if(b.typeId[i]!==roof.type) continue;
    const hp=Math.min(b.maxHp[i],b.hp[i]+roof.maxHp*share);
    if(hp!==b.hp[i]) {b.hp[i]=hp;changed=true;}
  }
  for(const wall of blueprint.walls) {
    if((wall.level ?? 1)>w.villageLevel) continue;
    const k=wall.tile;
    if(w.map.building[k]>=0) continue;
    // A surviving tower from a former higher level remains standing, but the
    // downgraded village cannot restore it until that level is bought again.
    if(w.map.wall[k] && (w.map.wall[k]!==wall.kind || w.map.wallMaxHp[k]>wall.maxHp+0.001)) continue;
    if(!w.map.wall[k]) {
      setWall(w,k%w.map.w,Math.floor(k/w.map.w),wall.kind,wall.maxHp/Math.max(1e-9,w.mods.wallHp),wall.tier);
      w.map.wallHp[k]=0;
    }
    const hp=Math.min(w.map.wallMaxHp[k],w.map.wallHp[k]+wall.maxHp*share);
    if(hp!==w.map.wallHp[k]) {w.map.wallHp[k]=hp;changed=true;}
  }
  if(changed) {
    w.map.invalidateRubbleIndex();w.map.wallChanged();w.dirty.flow=true;w.dirty.jobs=true;w.dirty.districts=true;
    recomputePopCap(w);recomputeStorage(w);recomputeManaCap(w);recount(w);
  }
  return changed;
}

/** Apply only newly unlocked plots and upgrade surviving wall sections at the same health fraction. */
export function applyVillagePreset(w: World, type: VillageType, level: number, fort = villageLevelStats(type,level).fort): boolean {
  const next=Math.max(1,Math.min(7,Math.floor(level))), previous=w.villageLevel;
  if(previous===next && w.config.villageType===type) return false;
  w.config.villageType=type;
  w.rung=next;
  if(previous>next) {
    // Capture lowers the level without demolishing the surviving village.
    // Lost structures may only recover to the newly affordable preset.
    const current=new Map(wallPlan(next).map(cell=>[w.map.idx(w.map.hearthX+cell.x,w.map.hearthY+cell.y),cell]));
    const tier=Math.min(3,Math.max(wallTier(next),1+fort));
    for(const wall of w.villageBlueprint?.walls ?? []) {
      const cell=current.get(wall.tile);
      if(cell) Object.assign(wall,{kind:cell.kind,tier,maxHp:wallHealth(cell.kind,tier,next)*w.mods.wallHp,level:1});
      else if((wall.level ?? 1)>1) wall.level=Math.max(wall.level!,next+1);
    }
    w.villageLevel=next;
    return true;
  }
  // Existing saved villages keep their actual old streets and damage. Tagging a
  // legacy layout is not permission to demolish it or resurrect missing roofs.
  if(previous===0 && w.buildings.slots.count>1) { w.villageLevel=next; rememberLayout(w); return true; }
  w.villageBlueprint ??= {roofs:[],walls:[]};
  const bank=villageBank(w);
  const oldWalls=new Set(previous>0 ? wallPlan(previous).map(p=>`${p.x}/${p.y}`) : []);
  const tier=Math.min(3,Math.max(wallTier(next),1+fort)), cx=w.map.hearthX,cy=w.map.hearthY;
  for(const cell of wallPlan(next)) {
    const x=cx+cell.x,y=cy+cell.y;
    if(!dry(w,x,y)) continue;
    const k=w.map.idx(x,y);
    if(w.map.building[k]>=0) continue;
    // A destroyed earlier section remains a breach, including when a tower
    // would have upgraded that section. New tower interior tiles remain new.
    const ratio=w.map.wall[k] && w.map.wallMaxHp[k]>0 ? w.map.wallHp[k]/w.map.wallMaxHp[k] : 1;
    const hp=wallHealth(cell.kind,tier,next);
    const firstLevel=Array.from({length:next},(_,i)=>i+1).find(l=>wallPlan(l).some(p=>p.x===cell.x&&p.y===cell.y&&p.kind===cell.kind)) ?? next;
    const record={tile:k,kind:cell.kind,tier,maxHp:hp*w.mods.wallHp,level:firstLevel},saved=w.villageBlueprint.walls.find(r=>r.tile===k);
    if(saved) Object.assign(saved,record);else w.villageBlueprint.walls.push(record);
    if(oldWalls.has(`${cell.x}/${cell.y}`) && w.map.wall[k]===WallKind.None) continue;
    setWall(w,x,y,cell.kind,hp,tier);
    w.map.wallHp[k]=w.map.wallMaxHp[k]*Math.max(0,Math.min(1,ratio));
  }
  if(previous===0) {
    // No stock is created by visiting a province. Its monthly yield already
    // goes to the campaign treasury or an owned city warehouse.
    for(const key of RESOURCE_KEYS) w.res[key]=0;
    w.thralls=0;
    villageStreets(w,bank);
  }
  for(const plot of villageLayoutPlan(type,next)) {
    if(plot.level<=previous) continue;
    const key=`${plot.id}/${plot.x}/${plot.y}`;
    // A previously constructed plot may be a ruin after capture. Buying the
    // level again unlocks its recovery; it does not duplicate or resurrect it.
    if(w.villageBlueprint.roofs.some(r=>r.key===key)) continue;
    const site=stampPlot(w,plot,bank);
    if(site>=0) w.villageBlueprint.roofs.push({type:w.buildings.typeId[site],x:w.buildings.tx[site],y:w.buildings.ty[site],tier:w.buildings.tier[site],maxHp:w.buildings.maxHp[site],level:plot.level,key});
    if(site>=0&&(Math.abs(plot.x)>VILLAGE_WALL_RADIUS||Math.abs(plot.y)>VILLAGE_WALL_RADIUS)) {
      const point:[number,number]=[w.buildings.tx[site]-1,w.buildings.ty[site]];
      paintRoad(w,villageRoad(w,villageGate(w,point),point,bank));
    }
  }
  w.villageLevel=next;
  rememberLayout(w);
  recomputePopCap(w);recomputeStorage(w);recomputeManaCap(w);
  // Only the new level adds residents. Re-entering a damaged village never
  // restores casualties, and new residents are not additional campaign levies.
  const room=Math.max(0,Math.min(w.popCap,16+2*(next-1))-w.pop);
  const added=previous===0 ? room : Math.min(room,2*(next-previous));
  for(let j=0;j<added;j++) {
    const i=spawnUnit(w,VILLAGER_TYPE,Team.Player,cx+3+(j%4),cy+3+Math.floor(j/4));
    if(i>=0) w.pop++;
  }
  recount(w);w.counts.villagers=w.pop;
  w.map.terrainVersion++;w.dirty.flow=true;w.dirty.jobs=true;w.dirty.districts=true;
  return true;
}

/** Cheap until a level changes; used before visits, sieges and the shared campaign tick. */
export function syncVillageWorld(realm: World, town: World): boolean {
  const c=realm.campaign, p=town.config.province;
  if(!c||p===undefined||!c.provinces[p]||town.battle) return false;
  if(!isVillageHolding(c,p)) { town.config.villageType='';return false; }
  const initial=town.villageLevel===0;
  let changed=applyVillagePreset(town,villageTypeOf(c,p),villageLevel(c,p),c.provinces[p].fort);
  if(initial) town.villageLastTurn=c.turn;
  if(c.turn>town.villageLastTurn) {
    const months=c.turn-town.villageLastTurn;
    town.villageLastTurn=c.turn;
    if(c.provinces[p].owner===c.playerNation && c.provinces[p].siegeBy<0 && c.pending?.province!==p) changed=recoverVillage(town,months)||changed;
  }
  return changed;
}
